<div class="page-header">
  <div class="title-section">
    <h2 class="page-title">Gestion des types de documents</h2>
    <p class="page-subtitle">Gérez les versions et les types de documents requis pour les projets</p>
  </div>
  <div class="header-actions">
    @if (successMessage()) {
    <span class="saving-indicator success-indicator">
      <mat-icon aria-hidden="true">check_circle</mat-icon>
      {{ successMessage() }}
    </span>
    }
    @if (loading()) {
    <span class="saving-indicator">
      <mat-icon aria-hidden="true" class="spin">sync</mat-icon>
      Chargement...
    </span>
    }
    @if (canCreateNewVersion) {
    <button class="btn btn-outline btn-with-icon" (click)="openCreateDialog()">
      <mat-icon aria-hidden="true">add</mat-icon>
      Créer une nouvelle version
    </button>
    }
  </div>
</div>

@if (errorMessage()) {
<div class="alert alert-error mb-4">
  {{ errorMessage() }}
  <button class="close-alert" (click)="errorMessage.set('')">×</button>
</div>
}

<div class="doc-type-layout">

  <!-- Sidebar : liste des versions -->
  <aside class="versions-sidebar card" [class.collapsed]="isSidebarCollapsed()">
    <div class="sidebar-header">
      <h3>Historique des versions</h3>
      <button class="btn-toggle-sidebar" (click)="toggleSidebar()" title="Réduire/Agrandir">
        <mat-icon>{{ isSidebarCollapsed() ? 'chevron_right' : 'chevron_left' }}</mat-icon>
      </button>
    </div>
    <div class="versions-list">
      @if (versions().length === 0) {
      <div class="loading-sidebar">Aucune version configurée</div>
      }
      @for (v of versions(); track v.id) {
      <button class="version-item" [class.active]="selectedVersion()?.id === v.id" (click)="selectVersion(v)"
        type="button">
        <span class="version-badge">Version</span>
        <span class="version-name">{{ v.version }}</span>
        @if (v.validated) {
        <span class="validated-label">Validée</span>
        } @else {
        <span class="draft-label">Brouillon</span>
        }
      </button>
      }
    </div>
  </aside>

  <!-- Contenu principal -->
  <div class="main-content">

    @if (!loading() && selectedVersion() && selectedVersion()!.validated) {
    <div class="alert alert-warning">
      Cette version ({{ selectedVersion()!.version }}) a été validée. Les modifications sont bloquées.
      Créez une nouvelle version pour apporter des modifications.
    </div>
    }

    @if (selectedVersion()) {
    <div class="content-header card shadow-sm">
      <div class="content-title-row">
        <div>
          <h3 class="content-title">Version : {{ selectedVersion()!.version }}</h3>
          <span class="content-subtitle">{{ selectedVersion()!.docTypes.length || 0 }} type(s) de document</span>
        </div>
        <div class="content-actions">
          @if (!selectedVersion()!.validated) {
          <button class="btn btn-success btn-with-icon" (click)="requestValidateVersion()">
            <mat-icon aria-hidden="true">check_circle</mat-icon>
            Valider la version
          </button>
          <button class="btn btn-primary btn-with-icon" (click)="openAddDocType()">
            <mat-icon aria-hidden="true">add</mat-icon>
            Ajouter un type
          </button>
          }
        </div>
      </div>

      <!-- Formulaire ajout type inline -->
      @if (showAddDocTypeForm) {
      <form [formGroup]="addDocTypeForm" (ngSubmit)="submitAddDocType()" class="inline-form">
        <div class="form-row">
          <input type="text" formControlName="name" class="form-control compact-input"
            placeholder="Nom du type de document" style="flex:1">
          <input type="number" formControlName="maxDocs" class="form-control compact-input"
            placeholder="Max docs (vide=illimité)" style="width:180px">
          <label class="checkbox-group">
            <input type="checkbox" formControlName="mandatory">
            Obligatoire
          </label>
          <label class="checkbox-group">
            <input type="checkbox" formControlName="commentMandatory">
            Commentaire obligatoire
          </label>
          <label class="checkbox-group">
            <input type="checkbox" formControlName="docReferenceMandatory">
            Réf. obligatoire
          </label>
          <input type="text" formControlName="commentPlaceholder" class="form-control compact-input"
            placeholder="Placeholder commentaire" style="width:200px">
          <button type="submit" class="btn btn-primary btn-sm" [disabled]="addDocTypeForm.invalid">Ajouter</button>
          <button type="button" class="btn btn-outline btn-sm" (click)="cancelAddDocType()">Annuler</button>
        </div>
      </form>
      }
    </div>

    <div class="table-responsive card shadow-sm">
      <table class="modern-table">
        <thead>
          <tr>
            <th>Nom du type</th>
            <th>Max docs</th>
            <th>Document Obligatoire</th>
            <th>Commentaire Obligatoire</th>
            <th>Réf. Obligatoire</th>
            <th>État</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (ref of selectedVersion()!.docTypes; track ref.id) {
          <tr [class.archived-row]="ref.archived">
            @if (editingDocType()?.id === ref.id) {
            <td colspan="7">
              <form [formGroup]="editDocTypeForm" (ngSubmit)="submitEditDocType()" class="inline-form">
                <div class="form-row">
                  <input type="text" formControlName="name" class="form-control compact-input" style="flex:1">
                  <input type="number" formControlName="maxDocs" class="form-control compact-input"
                    placeholder="Max docs" style="width:160px">
                  <label class="checkbox-group">
                    <input type="checkbox" formControlName="mandatory">
                    Obligatoire
                  </label>
                  <label class="checkbox-group">
                    <input type="checkbox" formControlName="commentMandatory">
                    Commentaire obligatoire
                  </label>
                  <label class="checkbox-group">
                    <input type="checkbox" formControlName="docReferenceMandatory">
                    Réf. obligatoire
                  </label>
                  <input type="text" formControlName="commentPlaceholder" class="form-control compact-input"
                    placeholder="Placeholder" style="width:160px">
                  <button type="submit" class="btn btn-primary btn-sm"
                    [disabled]="editDocTypeForm.invalid">Enregistrer</button>
                  <button type="button" class="btn btn-outline btn-sm" (click)="cancelEditDocType()">Annuler</button>
                </div>
              </form>
            </td>
            } @else {
            <td class="font-bold">{{ ref.name }}</td>
            <td>{{ ref.maxDocs || 'Illimité' }}</td>
            <td>
              @if (ref.mandatory) {
              <span class="badge-mandatory">Oui</span>
              } @else {
              <span class="badge-optional">Non</span>
              }
            </td>
            <td>
              @if (ref.commentMandatory) {
              <span class="badge-mandatory">Oui</span>
              } @else {
              <span class="badge-optional">Non</span>
              }
              @if (ref.commentPlaceholder) {
              <small class="d-block text-muted" [title]="'Placeholder: ' + ref.commentPlaceholder">
                ({{ ref.commentPlaceholder.length > 15 ? ref.commentPlaceholder.substring(0, 15) + '...' :
                ref.commentPlaceholder }})
              </small>
              }
            </td>
            <td>
              @if (ref.docReferenceMandatory) {
              <span class="badge-mandatory">Oui</span>
              } @else {
              <span class="badge-optional">Non</span>
              }
            </td>
            <td>
              @if (ref.archived) {
              <span class="badge-archived">Archivé</span>
              } @else {
              <span class="badge-active-doc">Actif</span>
              }
            </td>
            <td class="actions-cell">
              <div class="actions-wrapper">
                @if (!ref.archived && !selectedVersion()!.validated) {
                <button class="btn-icon" (click)="openEditDocType(ref)" title="Modifier">
                  <mat-icon aria-hidden="true">edit</mat-icon>
                </button>
                <button class="btn-icon delete" (click)="requestDeleteDocType(ref)" title="Supprimer / Archiver">
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
                }
              </div>
            </td>
            }
          </tr>
          } @empty {
          <tr class="empty-row">
            <td colspan="7">
              <div class="empty-state">
                <mat-icon class="empty-icon" aria-hidden="true">inbox</mat-icon>
                <p>Aucun type de document dans cette version</p>
              </div>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>

    } @else {
    <div class="empty-state" style="padding: 3rem;">
      <mat-icon class="empty-icon" aria-hidden="true">layers</mat-icon>
      <p>Sélectionnez une version pour voir ses types de documents</p>
    </div>
    }
  </div>
</div>

<!-- Dialog création nouvelle version -->
@if (showCreateDialog) {
<app-create-doc-version-dialog [canDuplicate]="!!selectedVersion()" (confirmed)="handleCreateConfirmed($event)"
  (cancelled)="showCreateDialog = false">
</app-create-doc-version-dialog>
}

@if (showDeleteConfirm()) {
<app-confirmation-dialog [dialogTitle]="getDeleteTitle()" [message]="getDeleteMessage()"
  [confirmLabel]="canDeleteDocType() ? 'Supprimer' : 'Archiver'" [isDanger]="true" (confirm)="confirmDeleteDocType()"
  (cancel)="cancelDeleteDocType()">
</app-confirmation-dialog>
}

@if (showValidateConfirm()) {
<app-confirmation-dialog dialogTitle="Valider la version"
  [message]="'Voulez-vous valider la version &quot;' + selectedVersion()!.version + '&quot; ? Les modifications seront bloquées définitivement.'"
  confirmLabel="Valider" [isDanger]="false" (confirm)="confirmValidateVersion()" (cancel)="cancelValidateVersion()">
</app-confirmation-dialog>
}